import { isNil, isNotNil } from "@etudescarre/utils";
import { useState } from "react";
import { apiClient } from "@/lib/api/client";
import { useYardContext } from "@/providers/yard-context";
import { NoticeForm } from "../notice-form";
import { ReportValidation } from "./report-validation";

export type ReportEntryStepProps = {
  reportId: number;
  /** A received report needs its PDF before validation; a deposit does not. */
  hasPdf: boolean;
  intro: string;
  onClose: () => void;
};

/**
 * Second step of a report saved as a draft, shared by screens 022 and 033:
 * enter its avis, then validate. Leaving keeps the draft.
 */
export function ReportEntryStep({
  reportId,
  hasPdf,
  intro,
  onClose,
}: ReportEntryStepProps) {
  const { yard } = useYardContext();
  const [pdfAttached, setPdfAttached] = useState(hasPdf);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const handlePdfChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (isNil(file)) return;
    setUploading(true);
    setPdfError(null);
    try {
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/reports/{reportId}/pdf",
        {
          params: {
            path: { yardId: String(yard.id), reportId },
          },
          body: { pdf: file },
          bodySerializer() {
            const fd = new FormData();
            fd.append("pdf", file, file.name);
            return fd;
          },
        },
      );
      if (error) {
        setPdfError(error.error);
        e.target.value = "";
        return;
      }
      setPdfAttached(true);
    } catch {
      setPdfError("Le fichier n'a pas pu être envoyé, réessayez");
      e.target.value = "";
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onClose}
        className="text-sm text-blue-600 hover:underline dark:text-blue-400"
      >
        Retour aux actions
      </button>

      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
        Avis du rapport
      </h3>
      <p className="text-xs text-zinc-500 dark:text-zinc-400">{intro}</p>

      {!pdfAttached && (
        <div>
          <label
            htmlFor="reportDraftPdf"
            className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
          >
            Rapport (PDF)
          </label>
          <p className="mb-1 text-xs text-zinc-500">
            Le PDF du rapport est requis pour valider la saisie.
          </p>
          <input
            id="reportDraftPdf"
            type="file"
            accept=".pdf"
            disabled={uploading}
            onChange={handlePdfChange}
            className="w-full text-sm text-zinc-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:text-zinc-400 dark:file:bg-blue-900/20 dark:file:text-blue-400"
          />
          {isNotNil(pdfError) && (
            <p className="mt-1 text-xs text-red-600">{pdfError}</p>
          )}
        </div>
      )}

      <NoticeForm yardId={String(yard.id)} reportId={reportId} />
      <ReportValidation reportId={reportId} onValidated={onClose} />
    </div>
  );
}
